/** Settings panel and the START/STOP control. */

import { patchSettings, settings$, watchlist$, keys$ } from "../state/store.ts";
import type { Settings } from "../state/types.ts";
import { isRunning, pokeAll, recomputeAll, start, stop } from "../engine/scheduler.ts";
import { TORN_CACHE_DELAY_SEC } from "../api/torn.ts";
import { ANON_FEE_PCT, MARKET_FEE_PCT } from "../engine/scoring.ts";
import {
  notificationPermission,
  requestNotificationPermission,
} from "../alerts/notify.ts";
import { primeAudio } from "../alerts/sound.ts";
import { append, card, el, labelWithHelp, on } from "./dom.ts";
import { clamp } from "../util/format.ts";

interface NumberFieldOptions {
  label: string;
  help: string;
  min: number;
  max: number;
  step?: number;
  value: () => number;
  onChange: (value: number) => void;
  suffix?: string;
}

function numberField(options: NumberFieldOptions): { root: HTMLElement; sync: () => void } {
  const input = el("input", {
    type: "number",
    class: "form-control form-control-sm text-center mono",
    min: options.min,
    max: options.max,
    step: options.step ?? 1,
  }) as HTMLInputElement;

  const commit = (raw: number) => {
    const next = clamp(Math.round(raw), options.min, options.max);
    input.value = String(next);
    options.onChange(next);
  };

  on(input, "change", () => commit(Number(input.value)));

  const stepper = (delta: number, label: string) => {
    const button = el("button", {
      type: "button",
      class: "btn btn-sm btn-outline-secondary",
      text: label,
      "aria-label": `${label === "+" ? "Increase" : "Decrease"} ${options.label}`,
    });
    on(button, "click", () => commit(Number(input.value) + delta));
    return button;
  };

  const root = el(
    "div",
    {},
    el("div", { class: "control-label mb-1" }, labelWithHelp(options.label, options.help)),
    el(
      "div",
      { class: "input-group input-group-sm" },
      stepper(-(options.step ?? 1), "−"),
      input,
      stepper(options.step ?? 1, "+"),
    ),
  );

  return { root, sync: () => (input.value = String(options.value())) };
}

interface ToggleOptions {
  label: string;
  help: string;
  value: () => boolean;
  onChange: (value: boolean) => void;
  disabled?: () => string | null;
}

function toggleField(options: ToggleOptions): { root: HTMLElement; sync: () => void } {
  const id = `t_${Math.random().toString(36).slice(2, 9)}`;
  const input = el("input", {
    class: "form-check-input",
    type: "checkbox",
    role: "switch",
    id,
  }) as HTMLInputElement;

  on(input, "change", () => options.onChange(input.checked));

  const hint = el("div", { class: "tiny text-body-secondary d-none" });

  const root = el(
    "div",
    {},
    el("div", { class: "control-label mb-1" }, labelWithHelp(options.label, options.help)),
    el(
      "div",
      { class: "form-check form-switch mb-0" },
      input,
      el("label", { class: "form-check-label tiny", for: id, text: "" }),
    ),
    hint,
  );

  const sync = () => {
    input.checked = options.value();
    const reason = options.disabled?.() ?? null;
    input.disabled = reason !== null;
    hint.textContent = reason ?? "";
    hint.classList.toggle("d-none", reason === null);
    const label = root.querySelector<HTMLLabelElement>(".form-check-label");
    if (label) label.textContent = input.checked ? "On" : "Off";
  };

  return { root, sync };
}

export function createSettingsPanel(): HTMLElement {
  const grid = el("div", { class: "row row-cols-2 g-3" });
  const syncers: Array<() => void> = [];

  const add = (field: { root: HTMLElement; sync: () => void }) => {
    append(grid, el("div", { class: "col" }, field.root));
    syncers.push(field.sync);
  };

  const patch = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    patchSettings({ [key]: value } as Partial<Settings>);
    recomputeAll();
  };

  add(
    numberField({
      label: "Profit %",
      help:
        "Minimum discount for a green deal, measured against what a resale really pays you — " +
        "market average minus any fee toggled on below. Higher = fewer but better alerts.",
      min: 0,
      max: 99,
      value: () => settings$.get().profitPct,
      onChange: (value) => patch("profitPct", value),
    }),
  );

  add(
    numberField({
      label: "Interval (s)",
      help:
        `How often each item is polled. Torn caches market data for ${TORN_CACHE_DELAY_SEC}s, ` +
        `so anything faster spends API calls on identical data — the engine clamps to ${TORN_CACHE_DELAY_SEC}s.`,
      min: 5,
      max: 600,
      step: 5,
      value: () => settings$.get().intervalSec,
      onChange: (value) => {
        patch("intervalSec", value);
        pokeAll();
      },
    }),
  );

  add(
    numberField({
      label: "Min units",
      help:
        "Ignores listings carrying fewer units than this — no alert, no BUY link, and they " +
        "can't become a row's best price. 1 means no filter. Costs no extra API calls.",
      min: 1,
      max: 10_000,
      value: () => settings$.get().minUnits,
      onChange: (value) => patch("minUnits", value),
    }),
  );

  add(
    numberField({
      label: "Bazaar every N ticks",
      help:
        "Bazaar data comes from weav3r, which caches for ~60s. Checking every tick rarely " +
        "surfaces anything new. weav3r calls don't count against Torn's rate limit.",
      min: 1,
      max: 10,
      value: () => settings$.get().bazaarTickRatio,
      onChange: (value) => patch("bazaarTickRatio", value),
    }),
  );

  add(
    toggleField({
      label: `Market fee ${MARKET_FEE_PCT}%`,
      help:
        `Torn charges the seller ${MARKET_FEE_PCT}% on every item-market sale. With this on, ` +
        "deals are scored against what a resale actually pays out instead of the raw market average.",
      value: () => settings$.get().marketFeeEnabled,
      onChange: (value) => patch("marketFeeEnabled", value),
    }),
  );

  add(
    toggleField({
      label: `Anonymous +${ANON_FEE_PCT}%`,
      help:
        `Listing anonymously costs another ${ANON_FEE_PCT}% on top of the ${MARKET_FEE_PCT}% sales ` +
        `fee — ${MARKET_FEE_PCT + ANON_FEE_PCT}% total. Turn on if you always sell anonymously.`,
      value: () => settings$.get().anonFeeEnabled,
      onChange: (value) => patch("anonFeeEnabled", value),
      disabled: () =>
        settings$.get().marketFeeEnabled ? null : `Turn on Market fee ${MARKET_FEE_PCT}% first.`,
    }),
  );

  add(
    toggleField({
      label: "Bazaars",
      help:
        "Includes player bazaars, not just the item market, pulled from weav3r.dev. " +
        "Third-party cache delay of up to ~60s.",
      value: () => settings$.get().bazaarEnabled,
      onChange: (value) => patch("bazaarEnabled", value),
    }),
  );

  add(
    toggleField({
      label: "Verify bazaars",
      help:
        "Before alerting on a bazaar deal, re-check the seller's bazaar directly through Torn " +
        "to confirm the listing still exists at that price. Costs one API call per verified hit " +
        "and kills most 'already claimed' false alarms.",
      value: () => settings$.get().bazaarVerify,
      onChange: (value) => patch("bazaarVerify", value),
      disabled: () => (settings$.get().bazaarEnabled ? null : "Turn on Bazaars first."),
    }),
  );

  add(
    toggleField({
      label: "Only under MV",
      help: "Discards any listing priced at or above market value, whatever the other settings say.",
      value: () => settings$.get().onlyUnderMv,
      onChange: (value) => patch("onlyUnderMv", value),
    }),
  );

  add(
    toggleField({
      label: "Sound",
      help: "Plays a short tone on each new green deal.",
      value: () => settings$.get().soundEnabled,
      onChange: (value) => {
        if (value) primeAudio();
        patch("soundEnabled", value);
      },
    }),
  );

  add(
    toggleField({
      label: "Notifications",
      help:
        "Desktop notification for green deals, even with the tab in the background. " +
        "Requires browser permission.",
      value: () => settings$.get().notifEnabled,
      onChange: (value) => {
        patch("notifEnabled", value);
        if (value) void requestNotificationPermission().then(syncAll);
      },
    }),
  );

  // ------------------------------------------------------------- run control

  const runButton = el("button", { type: "button", class: "btn btn-sm btn-primary fw-bold" });
  const summaryBadge = el("span", { class: "badge text-bg-light mono" });

  on(runButton, "click", () => {
    // First click doubles as the user gesture that unlocks WebAudio.
    primeAudio();
    if (isRunning()) stop();
    else start();
    syncAll();
  });

  const notifyPrompt = el("div", { class: "alert alert-warning tiny py-2 px-3 mt-3 mb-0 d-none" });
  const notifyButton = el("button", {
    type: "button",
    class: "btn btn-sm btn-warning ms-2",
    text: "Enable notifications",
  });
  on(notifyButton, "click", () => void requestNotificationPermission().then(syncAll));

  const bazaarNote = el(
    "div",
    { class: "alert alert-secondary tiny py-2 px-3 mt-3 mb-0" },
    el("strong", { text: "Heads up on bazaar deals: " }),
    "listings come from ",
    el("a", {
      href: "https://weav3r.dev",
      target: "_blank",
      rel: "noopener noreferrer",
      text: "weav3r.dev",
    }),
    " — a third-party aggregator with a server-side cache of up to ~60 seconds. Item-market " +
      "data comes directly from Torn and is live; bazaar data is best-effort.",
  );

  function syncAll(): void {
    for (const sync of syncers) sync();

    const settings = settings$.get();
    const running = isRunning();

    runButton.textContent = running ? "■ STOP" : "▶ START";
    runButton.className = `btn btn-sm fw-bold ${running ? "btn-danger" : "btn-primary"}`;
    runButton.disabled = !running && keys$.get().every((entry) => entry.health !== "valid");
    runButton.title = runButton.disabled ? "Add a valid API key first." : "";

    const fee =
      (settings.marketFeeEnabled ? MARKET_FEE_PCT : 0) +
      (settings.marketFeeEnabled && settings.anonFeeEnabled ? ANON_FEE_PCT : 0);

    summaryBadge.textContent = [
      `${settings.profitPct}%`,
      `${settings.intervalSec}s`,
      fee > 0 ? `−${fee}% fee` : null,
      settings.minUnits > 1 ? `≥${settings.minUnits}u` : null,
      settings.bazaarEnabled ? "bz" : null,
      settings.bazaarEnabled && settings.bazaarVerify ? "✓" : null,
    ]
      .filter(Boolean)
      .join(" · ");

    const permission = notificationPermission();
    const needsPrompt = settings.notifEnabled && permission === "default";
    notifyPrompt.classList.toggle("d-none", !needsPrompt);
    if (needsPrompt) {
      notifyPrompt.textContent = "Browser notifications need permission.";
      append(notifyPrompt, notifyButton);
    }

    bazaarNote.classList.toggle("d-none", !settings.bazaarEnabled);
  }

  settings$.subscribe(syncAll, false);
  keys$.subscribe(syncAll, false);
  watchlist$.subscribe(syncAll, false);
  syncAll();

  const body = el("div", {}, grid, notifyPrompt, bazaarNote);
  const header = el("div", { class: "d-flex align-items-center gap-2" }, summaryBadge, runButton);

  return card("Settings", body, header);
}
